import { GUIDANCE_LEVEL, GUIDE_TYPE } from "../../constants/orderForms";
import {
  getSummaryDisplayValue,
  getSummaryListDisplayValue,
} from "../../Utils/utility";

const getLatestServiceValue = (service, fieldName) => {
  const value = service?.[fieldName];
  if (Array.isArray(value)) {
    return value.length ? value[value.length - 1] : undefined;
  }
  return value;
};

const getGuideTypeValues = (service) => {
  const value = service?.guideType;
  if (Array.isArray(value)) {
    return Array.from(new Set(value.filter(Boolean)));
  }
  return value ? [value] : [];
};

const SurgicalGuidesOrderSummary = ({ orderData, selectedServiceCategory }) => {
  const surgicalCaseDetails = orderData?.caseDetails ?? orderData ?? {};
  const surgicalServices = surgicalCaseDetails?.services ?? orderData?.services ?? [];

  return (
    <>
      {surgicalServices?.map((service) => {
        const guideTypeValues = getGuideTypeValues(service);
        const guideType = guideTypeValues.join(", ");
        const conversionDentureAvailable = getLatestServiceValue(
          service,
          "conversionDentureAvailable"
        );
        const digitalConversionDenture = getLatestServiceValue(
          service,
          "digitalConversionDenture"
        );
        const densahBursUsed = getLatestServiceValue(service, "densahBursUsed");
        const densahBurGuideDesired = getLatestServiceValue(service, "densahBurGuideDesired");
        const guidanceLevel = getLatestServiceValue(service, "guidanceLevel");
        const serviceDetails = selectedServiceCategory?.catservices?.find(
          (d) => d?.serviceObjId === service?.serviceObjId
        );
        if (serviceDetails?.serviceObjId) {
          return (
            <div
              key={"serviceItem-" + serviceDetails?.serviceObjId}
              className="grid md:grid-cols-3 grid-cols-1 gap-y-8 gap-x-4 rounded-xl bg-backgroundV2 p-6 my-4"
            >
              <div className="TILE md:col-span-3 pb-4 border-b border-1 border-dotted border-primary">
                <p className="text-[15px] text-white font-semibold">Service Name</p>
                <p className="text-primary font-medium mt-2 uppercase">
                  {serviceDetails?.serviceName}
                </p>
              </div>
              {[
                "Single or multiple single implant surgical guide",
                "Bone regeneration surgical mesh",
                "Implant bridge surgical guide",
                "Soft tissue based surgical guide for All on X",
                "Full arch stackable surgical guides for All on X (Resin or Metal) or Overdentures",
              ].includes(serviceDetails?.serviceName) && (
                <>
                  <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                    <p className="text-[15px] text-white">Specific Guide Type</p>
                    <p className="text-[#d1d5db] font-medium mt-2">
                      {getSummaryDisplayValue(guideType)}
                    </p>
                  </div>
                  {guideTypeValues.includes(GUIDE_TYPE.BONE_SUPPORTED) && (
                    <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                      <p className="text-[15px] text-white">
                        If bone reduction is necessary and you have not requested it, can this be
                        incorporated into the plan?
                      </p>
                      <p className="text-[#d1d5db] font-medium mt-2">
                        {getSummaryDisplayValue(
                          getLatestServiceValue(service, "boneReductionInPlan")
                        )}
                      </p>
                    </div>
                  )}
                </>
              )}
              <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                <p className="text-[15px] text-white">
                  {serviceDetails?.serviceName === "Bone regeneration surgical mesh"
                    ? "Please select grafting site(s) where the mesh is required"
                    : serviceDetails?.serviceName === "Endodontic access or microsurgery guide"
                    ? "Please select teeth for endodontic access and/or microsurgery"
                    : "Please select implant site(s)"}
                </p>
                <p className="text-[#d1d5db] font-medium mt-2">
                  {(service?.[
                    serviceDetails?.serviceName === "Bone regeneration surgical mesh"
                      ? "graftingSiteTeeth"
                      : serviceDetails?.serviceName === "Endodontic access or microsurgery guide"
                      ? "endodonticAccessTeeth"
                      : "implantSiteTeeth"
                  ] ?? (serviceDetails?.serviceName !== "Bone regeneration surgical mesh" &&
                    serviceDetails?.serviceName !== "Endodontic access or microsurgery guide"
                    ? service?.implantSites : []))?.join(", ") || "Unavailable"}
                </p>
              </div>
              {[
                "Full arch stackable surgical guides for All on X (Resin or Metal) or Overdentures",
                "Soft tissue based surgical guide for All on X or Bar overdenture",
              ].includes(serviceDetails?.serviceName) && (
                <>
                  <div className="TILE md:col-span-2 pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                    <p className="text-[15px] text-white">
                      What additional stackable guide options are desired?
                    </p>
                    <p className="text-[#d1d5db] font-medium mt-2">
                      {getSummaryDisplayValue(
                        service?.additionalGuideOptions &&
                          Object.keys(service?.additionalGuideOptions || {})
                            .map((d) => `${d}(${service?.additionalGuideOptions?.[d]})`)
                            .join(", ")
                      )}
                    </p>
                  </div>
                </>
              )}
              {[
                "Full arch stackable surgical guides for All on X (Resin or Metal) or Overdentures",
              ].includes(serviceDetails?.serviceName) && (
                <>
                  <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                    <p className="text-[15px] text-white">
                      If no provisional denture has been selected, is a conversion denture
                      available?
                    </p>
                    <p className="text-[#d1d5db] font-medium mt-2">
                      {getSummaryDisplayValue(conversionDentureAvailable)}
                    </p>
                  </div>
                </>
              )}
              {[
                "Full arch stackable surgical guides for All on X (Resin or Metal) or Overdentures",
                "Soft tissue based surgical guide for All on X",
                "Soft tissue based surgical guide for All on X or Bar overdenture",
              ].includes(serviceDetails?.serviceName) &&
                !service?.additionalGuideOptions?.["Guided temporary Prosthesis"] &&
                conversionDentureAvailable === "No" && (
                  <>
                    <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                      <p className="text-[15px] text-white">
                        A digital conversion denture can also be provided. Is this required?
                      </p>
                      <p className="text-[#d1d5db] font-medium mt-2">
                        {getSummaryDisplayValue(digitalConversionDenture)}
                      </p>
                    </div>
                    <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                      <p className="text-[15px] text-white">Please indicate abutment preferences</p>
                      <p className="text-[#d1d5db] font-medium mt-2">
                        {getSummaryDisplayValue(
                          getLatestServiceValue(service, "abutmentPreferences")
                        )}
                      </p>
                    </div>
                  </>
                )}
              <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                <p className="text-[15px] text-white">Surgery Date</p>
                <p className="text-[#d1d5db] font-medium mt-2">
                  {getSummaryDisplayValue(getLatestServiceValue(service, "surgeryDate"))}
                </p>
              </div>
              <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                <p className="text-[15px] text-white">Preferred design software?</p>
                <p className="text-[#d1d5db] font-medium mt-2">
                  {getSummaryDisplayValue(
                    getLatestServiceValue(service, "preferredSoftware") ??
                      getLatestServiceValue(service, "preferredDesignSoftware")
                  )}
                </p>
              </div>
              {serviceDetails?.serviceName === "Endodontic access or microsurgery guide" ? (
                <>
                  <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                    <p className="text-[15px] text-white">Preferred Guided Access drill brand</p>
                    <p className="text-[#d1d5db] font-medium mt-2">
                      {getSummaryDisplayValue(
                        getLatestServiceValue(service, "guidedAccessDrillBrand")
                      )}
                    </p>
                  </div>
                  <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                    <p className="text-[15px] text-white">
                      Guided Access drill shank diameter (mm)
                    </p>
                    <p className="text-[#d1d5db] font-medium mt-2">
                      {getSummaryDisplayValue(service?.drillShankDiameter)}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                    <p className="text-[15px] text-white">Preferred Implant Company</p>
                    <p className="text-[#d1d5db] font-medium mt-2">
                      {getSummaryDisplayValue(
                        getLatestServiceValue(service, "preferredImplantCompany")
                      )}
                    </p>
                  </div>
                  <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                    <p className="text-[15px] text-white">Implant Line</p>
                    <p className="text-[#d1d5db] font-medium mt-2">
                      {getSummaryDisplayValue(getLatestServiceValue(service, "implantLine"))}
                    </p>
                  </div>
                </>
              )}
              {[
                "Bone regeneration surgical mesh",
                "Endodontic access or microsurgery guide",
              ].includes(serviceDetails?.serviceName) ? null : (
                <>
                  <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                    <p className="text-[15px] text-white">Preferred Pin Company</p>
                    <p className="text-[#d1d5db] font-medium mt-2">
                      {getSummaryDisplayValue(
                        getLatestServiceValue(service, "preferredPinCompany")
                      )}
                    </p>
                  </div>
                  <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                    <p className="text-[15px] text-white">Pin Line</p>
                    <p className="text-[#d1d5db] font-medium mt-2">
                      {getSummaryDisplayValue(getLatestServiceValue(service, "pinLine"))}
                    </p>
                  </div>
                  <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                    <p className="text-[15px] text-white">Implant Surgical Kit</p>
                    <p className="text-[#d1d5db] font-medium mt-2">
                      {getSummaryDisplayValue(
                        getLatestServiceValue(service, "implantSurgicalKit")
                      )}
                    </p>
                  </div>
                  <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                    <p className="text-[15px] text-white">Will Densah burs be used?</p>
                    <p className="text-[#d1d5db] font-medium mt-2">
                      {getSummaryDisplayValue(densahBursUsed)}
                    </p>
                  </div>
                  {densahBursUsed === "Yes" && (
                    <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                      <p className="text-[15px] text-white">Is a Densah bur guide desired?</p>
                      <p className="text-[#d1d5db] font-medium mt-2">
                        {getSummaryDisplayValue(densahBurGuideDesired)}
                      </p>
                    </div>
                  )}
                  {densahBursUsed === "Yes" && densahBurGuideDesired === "Yes" && (
                      <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                        <p className="text-[15px] text-white">
                          Sites at which Densah burs will be used
                        </p>
                        <p className="text-[#d1d5db] font-medium mt-2">
                          {getSummaryListDisplayValue(service?.densahBursTeeth)}
                        </p>
                      </div>
                    )}
                </>
              )}
              {serviceDetails?.serviceName === "Bone regeneration surgical mesh" ? null : (
                <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                  <p className="text-[15px] text-white">Will a flap be raised?</p>
                  <p className="text-[#d1d5db] font-medium mt-2">
                    {getSummaryDisplayValue(getLatestServiceValue(service, "flapBeRaised"))}
                  </p>
                </div>
              )}
              {[
                "Bone regeneration surgical mesh",
                "Endodontic access or microsurgery guide",
              ].includes(serviceDetails?.serviceName) ? null : (
                <>
                  <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                    <p className="text-[15px] text-white"> Choose Level of Guidance Desired</p>
                  <p className="text-[#d1d5db] font-medium mt-2">
                    {getSummaryDisplayValue(guidanceLevel)}
                  </p>
                  </div>
                  {guidanceLevel === GUIDANCE_LEVEL.FULLY_GUIDED && (
                    <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                      <p className="text-[15px] text-white">
                        {" "}
                        Will the prosthesis be used as a morphological template for the guide?
                      </p>
                      <p className="text-[#d1d5db] font-medium mt-2">
                        {getSummaryDisplayValue(
                          getLatestServiceValue(service, "prosthesisBeUsed")
                        )}
                      </p>
                    </div>
                  )}
                </>
              )}
              {["Endodontic access or microsurgery guide"].includes(
                serviceDetails?.serviceName
              ) ? null : (
                <>
                  <div className="TILE pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                    <p className="text-[15px] text-white">Choose Services Required</p>
                    <p className="text-[#d1d5db] font-medium mt-2">
                      {getSummaryListDisplayValue(service?.servicesRequired)}
                    </p>
                  </div>
                </>
              )}

              <div className="TILE md:col-span-2 pb-4 border-b border-1 border-dotted border-white border-opacity-20">
                <p className="text-[15px] text-white">Additional Instructions </p>
                <p className="text-[#d1d5db] font-medium mt-2">
                  {getSummaryDisplayValue(
                    getLatestServiceValue(service, "additionalInstructions")
                  )}
                </p>
              </div>
            </div>
          );
        }
        return null;
      })}
    </>
  );
};

export default SurgicalGuidesOrderSummary;
